/* eslint-disable no-param-reassign */
import type { Draft, PayloadAction } from "@reduxjs/toolkit";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { backOff } from "exponential-backoff";
import { numberOfBrickColours } from "@brickme/project-core/src/model/picture.ts";
import { corePictureToTransportPicture } from "@brickme/project-core/src/model/transport.ts";
import type {
	CompleteProjectMutation,
	CompleteProjectMutationVariables,
} from "~/api/complete-project-mutation.generated.ts";
import completeProjectMutation from "~/api/complete-project-mutation.graphql";
import type { State as WorkspaceState } from "~/features/workspace/state.ts";
import type { State as ReferenceState } from "~/features/reference/state.ts";
import {
	isBackendCommittedSaveState,
	type State as SaveProjectState,
} from "~/features/save-project/store-slice.ts";
import { getPossibleFrameVariantInStockForPicture } from "~/features/preview/frame.ts";
import { getNumberOfHangingStripPacksForPicture } from "~/features/preview/hanging-strips.ts";
import type { StoreThunkApiConfig } from "~/store-config.ts";
import type { OnceOffLoad } from "~/utils/loading.ts";
import {
	ensureLoadedOnceOffLoad,
	errorOnceOffLoad,
	isOnceOffLoaded,
	loadedOnceOffLoad,
	loadingOnceOffLoad,
} from "~/utils/loading.ts";
import createSerialisedBuild from "./create-serialised-build.ts";
import type { HangingOption } from "./hanging-option.ts";
import selectDefaultHangingType from "./select-default-hanging-type.ts";
import type { State as GiftState } from "~/features/gift/store-slice.ts";

const name = "order";

type AdjacentState = {
	readonly workspace: WorkspaceState;
	readonly reference: ReferenceState;
	readonly saveProject: SaveProjectState;
	readonly order: State;
	readonly gift: GiftState;
};

type CartItem = {
	readonly variantId: string;
	readonly quantity: number;
	readonly properties?: Record<string, string>;
};

const completeProject = createAsyncThunk<
	readonly CartItem[],
	undefined,
	StoreThunkApiConfig<AdjacentState>
>(`${name}/completeProject`, async (_, { extra: { apiClient }, getState }) => {
	const {
		reference: { systemPalette, preassemblyProduct, frames, hangingStrips },
		workspace: { openProject },
		order: { hangingOption, wantPreassembly },
		gift: { giftRedemption },
	} = getState();
	if (!openProject) {
		throw new Error("No open project");
	}

	const savedProjectId = await backOff(
		async () => {
			const {
				saveProject: { openedProjectSaveState },
			} = getState();
			if (
				!openedProjectSaveState ||
				!isBackendCommittedSaveState(openedProjectSaveState)
			) {
				throw new Error(`Project not saved yet`);
			}
			return Promise.resolve(openedProjectSaveState.id);
		},
		{
			startingDelay: 100,
			maxDelay: 300,
			timeMultiple: 1.1,
			numOfAttempts: 200,
		},
	);

	const systemPaletteData = ensureLoadedOnceOffLoad(
		systemPalette,
		"System palette not loaded",
	);

	const {
		build: openBuild,
		project: { currentPicture },
	} = openProject;
	if (openBuild.operationsMissingSources.length > 0) {
		throw new Error("Can't complete yet, still working");
	}

	// Frame
	const frame =
		isOnceOffLoaded(frames) && hangingOption?.type === "frame"
			? frames.data.find((f) => f.id === hangingOption.id)
			: undefined;
	const frameVariant = frame
		? getPossibleFrameVariantInStockForPicture(currentPicture, frame)
		: undefined;

	// Hanging strips
	const chosenHangingStrips =
		isOnceOffLoaded(hangingStrips) && hangingOption?.type === "hangingStrips"
			? hangingStrips.data
			: undefined;

	const baseplateCount =
		currentPicture.numberOfBasePlates.width *
		currentPicture.numberOfBasePlates.height;

	// Preassembly (single product, no size variants)
	const preassemblyProductData = isOnceOffLoaded(preassemblyProduct)
		? preassemblyProduct.data
		: undefined;
	const preassemblyVariant = wantPreassembly
		? preassemblyProductData
		: undefined;

	const { build, palette } = createSerialisedBuild(
		systemPaletteData,
		openBuild.image,
		currentPicture.paletteMode,
	);
	const numberOfColours = numberOfBrickColours(openProject.build.brickCounts);
	const {
		completeProject: { shopifyProductVariantId, customProperties },
	} = await apiClient.request<
		CompleteProjectMutation,
		CompleteProjectMutationVariables
	>(completeProjectMutation, {
		input: {
			savedProjectId,
			picture: corePictureToTransportPicture(currentPicture, numberOfColours),
			palette,
			build,
			overrideColourPremium: giftRedemption ? true : undefined,
		},
	});

	return [
		{
			variantId: shopifyProductVariantId,
			quantity: 1,
			properties:
				customProperties || preassemblyVariant
					? {
							...Object.fromEntries(
								(customProperties ?? []).map(({ name, value }) => [
									name,
									value,
								]),
							),
							...(preassemblyVariant && {
								"Pre-assembly service": `${baseplateCount} baseplates`,
								_baseplate_count: String(baseplateCount),
								_pre_assembly_variantId: preassemblyVariant.productVariantId,
							}),
						}
					: undefined,
		},
		...(frameVariant
			? [{ variantId: frameVariant.productVariantId, quantity: 1 }]
			: []),
		...(chosenHangingStrips
			? [
					{
						variantId: chosenHangingStrips.productVariantId,
						quantity: getNumberOfHangingStripPacksForPicture(currentPicture),
					},
				]
			: []),
		...(preassemblyVariant
			? [
					{
						variantId: preassemblyVariant.productVariantId,
						quantity: baseplateCount,
						properties: {
							_pre_assembly_variantId: preassemblyVariant.productVariantId,
						},
					},
				]
			: []),
	];
});

type State = {
	readonly completion: OnceOffLoad<readonly CartItem[]>;
	readonly hangingOption?: HangingOption;
	readonly wantPreassembly: boolean;
};

const initialState: State = {
	completion: { type: "pending" },
	hangingOption: undefined,
	wantPreassembly: false,
};

const slice = createSlice({
	name,
	initialState,
	reducers: {
		setHangingOptionFrame: (
			state: Draft<State>,
			action: PayloadAction<string | undefined>,
		) => {
			if (action.payload) {
				state.hangingOption = {
					type: "frame",
					id: action.payload,
				};
			} else {
				state.hangingOption = undefined;
			}
		},
		removeHangingOption: (state: Draft<State>) => {
			state.hangingOption = undefined;
		},
		setHangingOption: (
			state: Draft<State>,
			action: PayloadAction<HangingOption | undefined>,
		) => {
			state.hangingOption = action.payload;
		},
		setHangingOptionHangingStrips: (state: Draft<State>) => {
			state.hangingOption = { type: "hangingStrips" };
		},
		setWantPreassembly: (
			state: Draft<State>,
			action: PayloadAction<boolean>,
		) => {
			state.wantPreassembly = action.payload;
		},
	},
	extraReducers(builder) {
		builder.addCase(completeProject.pending.type, (state: Draft<State>) => {
			state.completion = loadingOnceOffLoad;
		});
		builder.addCase(
			completeProject.fulfilled.type,
			(
				state: Draft<State>,
				action: Draft<ReturnType<(typeof completeProject)["fulfilled"]>>,
			) => {
				state.completion = loadedOnceOffLoad(action.payload);
			},
		);
		builder.addCase(
			completeProject.rejected.type,
			(
				state: Draft<State>,
				action: ReturnType<(typeof completeProject)["rejected"]>,
			) => {
				state.completion = errorOnceOffLoad(
					action.error.message ?? "There was an error",
				);
			},
		);
		builder.addCase(
			selectDefaultHangingType.fulfilled.type,
			(
				state: Draft<State>,
				action: ReturnType<(typeof selectDefaultHangingType)["fulfilled"]>,
			) => {
				state.hangingOption = action.payload;
			},
		);
	},
});

export { completeProject };
export const {
	setHangingOptionFrame,
	setHangingOption,
	setWantPreassembly,
	setHangingOptionHangingStrips,
	removeHangingOption,
} = slice.actions;
export const initialOrderState = initialState;
export type { CartItem };
export default slice.reducer;
